import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Trophy, ArrowRight, AlertCircle } from 'lucide-react';
import VoiceWave from '@/components/VoiceWave';
import { getBillingStatus } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'pending', plan: null, error: null });
  const attemptsRef = useRef(0);
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', error: 'Missing session ID' });
      return;
    }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      attemptsRef.current += 1;
      if (attemptsRef.current > 10) {
        setState((s) => ({ ...s, status: 'timeout' }));
        return;
      }
      try {
        const res = await getBillingStatus(sessionId);
        if (cancelled) return;
        const ps = res.data?.payment_status;
        if (ps === 'paid') {
          setState({ status: 'paid', plan: res.data.plan, error: null });
          await checkAuth();
          return;
        }
        if (res.data?.status === 'expired') {
          setState({ status: 'expired', error: 'Checkout session expired' });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        setState({ status: 'error', error: err?.response?.data?.detail || 'Could not verify payment' });
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6" data-testid="billing-success-page">
      <div className="max-w-md w-full text-center">
        {state.status === 'pending' && (
          <>
            <VoiceWave size="md" centerDot speed={1.4} />
            <p className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500 animate-pulse">
              Confirming your payment…
            </p>
          </>
        )}

        {state.status === 'paid' && (
          <div className="rounded-3xl bg-gradient-to-br from-amber-500/15 via-zinc-900 to-violet-900/20 border border-amber-500/30 p-8" data-testid="billing-paid">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-5">
              <Trophy className="w-10 h-10 text-amber-400" />
            </div>
            <p className="text-xs tracking-[0.3em] uppercase text-amber-300 mb-2">
              {state.plan === 'trial' ? 'Trial Active' : 'Premium Active'}
            </p>
            <h1 className="text-3xl font-serif text-white mb-3">You're in.</h1>
            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
              {state.plan === 'trial'
                ? "You've unlocked Lifetunes Premium for the next 7 days — every category, every challenge, every track. After your trial, you'll be auto-billed $7/month. Cancel anytime."
                : "Your subscription is active. Welcome to Lifetunes Premium."}
            </p>
            <div className="flex flex-col gap-3">
              <Link
                to="/confidence/library"
                className="inline-flex items-center justify-center gap-2 bg-white text-zinc-950 font-semibold px-6 py-3.5 rounded-full hover:bg-zinc-100 transition-colors"
                data-testid="billing-continue-btn"
              >
                Start exploring
                <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-xs text-zinc-500 hover:text-zinc-300"
              >
                Go to dashboard
              </button>
            </div>
          </div>
        )}

        {(state.status === 'error' || state.status === 'expired' || state.status === 'timeout') && (
          <div className="rounded-3xl bg-zinc-900/60 border border-red-500/30 p-8" data-testid="billing-error">
            <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-serif text-white mb-3">
              {state.status === 'timeout' ? "Still confirming..." : "Something went wrong"}
            </h1>
            <p className="text-sm text-zinc-400 mb-6">
              {state.status === 'timeout'
                ? "Your payment is still being verified. Check back in a minute or contact support."
                : state.error}
            </p>
            <Link
              to="/confidence/library"
              className="inline-flex items-center justify-center gap-2 bg-white text-zinc-950 font-semibold px-6 py-3 rounded-full hover:bg-zinc-100 transition-colors"
            >
              Back to Library
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
